import React from "react";
import PropTypes from "prop-types";

import { vasBand } from "../vasScale";
import { formatShortDate } from "./dateHelpers";
import { TREND_COLOR, TrendDot, TrendTip, useTrendTip } from "../trendChart";

/**
 * SVG "Status over time" chart for the trend modal. Renders a step function
 * across the complaint's audit history:
 *
 *   entry.sinceWhen ─── vas ─── entry.toWhen (or now, if this is the current
 *                                             segment) — then a vertical jump
 *                                             to the next entry's vas.
 *
 * VAS band colours are painted as horizontal background rects so the reader
 * can read severity by the band alone, without checking the y-axis.
 *
 * Deliberately minimal: no smoothing, no month tick marks, no zoom. The
 * audit table below the chart carries the precise values.
 */
const W = 680;
const H = 220;
const PAD = { l: 34, r: 16, t: 14, b: 34 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

const vasToY = (vas) => PAD.t + (1 - Math.max(0, Math.min(10, vas)) / 10) * IH;

const StatusOverTimeChart = ({ audit }) => {
  // White HTML tooltip in place of the native SVG <title>. Wrapper needs
  // position: relative (set on `.cc-chart` in the shared SCSS).
  const { wrapRef, tip, showTip, hideTip } = useTrendTip();

  // Delete events are ignored — they don't change severity, only lifecycle.
  // Server append order is retained as `_idx` and used as the sort tiebreak:
  // when two entries share the same `sinceWhen` (common now that duration-
  // based Adds normalise to UTC midnight, matching date-picker UpdateStatus
  // writes), the chronological sort falls back to recording order so the
  // step line draws in the sequence the clinician actually logged.
  const entries = (audit || [])
    .filter((e) => e && e.event !== "delete" && e.sinceWhen)
    .map((e, i) => ({ ...e, _t: new Date(e.sinceWhen).getTime(), _idx: i }))
    .filter((e) => !isNaN(e._t))
    .sort((a, b) => (a._t - b._t) || (a._idx - b._idx));

  if (entries.length === 0) {
    return <div className="cc-chart-empty">No status entries to chart yet.</div>;
  }

  const now = Date.now();
  const tMin = entries[0]._t;
  const lastEndT = entries[entries.length - 1].toWhen
    ? new Date(entries[entries.length - 1].toWhen).getTime()
    : now;
  // Guarantee a non-zero range so timeToX is well-defined for single-point audits.
  const tMax = Math.max(lastEndT, tMin + 24 * 60 * 60 * 1000);

  const timeToX = (t) =>
    tMax === tMin ? PAD.l + IW / 2 : PAD.l + ((t - tMin) / (tMax - tMin)) * IW;

  // Build the step-function path. Each entry contributes a horizontal segment
  // at its vas; between entries a vertical connector jumps to the next value
  // at the changeover time.
  let d = "";
  entries.forEach((e, i) => {
    const startT = e._t;
    const endT = e.toWhen
      ? new Date(e.toWhen).getTime()
      : i === entries.length - 1
      ? now
      : entries[i + 1]._t;
    const x1 = timeToX(startT);
    const x2 = timeToX(endT);
    const y = vasToY(e.vas);

    if (i === 0) {
      d += `M ${x1} ${y} L ${x2} ${y}`;
    } else {
      const prevY = vasToY(entries[i - 1].vas);
      d += ` L ${x1} ${prevY} L ${x1} ${y} L ${x2} ${y}`;
    }
  });

  return (
    <div className="cc-chart" ref={wrapRef}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        preserveAspectRatio="none"
        role="img"
        aria-label="Status over time"
      >
        {/* VAS band backgrounds — Absent (below 0.5), Mild, Moderate, Severe, Worst */}
        <rect x={PAD.l} y={vasToY(10)} width={IW} height={vasToY(9.5) - vasToY(10)} fill="#fee2e2" />
        <rect
          x={PAD.l}
          y={vasToY(9.5)}
          width={IW}
          height={vasToY(6.5) - vasToY(9.5)}
          fill="rgba(249,127,15,0.15)"
        />
        <rect x={PAD.l} y={vasToY(6.5)} width={IW} height={vasToY(3.5) - vasToY(6.5)} fill="#fef3c7" />
        <rect x={PAD.l} y={vasToY(3.5)} width={IW} height={vasToY(0) - vasToY(3.5)} fill="#d1fae5" />

        {/* Y-axis labels — 0, 3, 6, 9, 10 (matches the VAS band boundaries). */}
        {[0, 3, 6, 9, 10].map((v) => (
          <text
            key={v}
            x={PAD.l - 6}
            y={vasToY(v) + 3}
            textAnchor="end"
            fontSize={10}
            fill="#6b7280"
          >
            {v}
          </text>
        ))}

        {/* Step function line */}
        <path d={d} stroke={TREND_COLOR} strokeWidth={2} fill="none" strokeLinejoin="miter" />

        {/* Dots at each entry's start — white HTML tooltip on hover (via
            useTrendTip) so the user can read the exact "since when" date +
            VAS band without leaving the chart. Native SVG <title> dropped
            so we control the pill's appearance across OS/browsers. */}
        {entries.map((e, i) => {
          const band = vasBand(e.vas);
          const text = `${formatShortDate(new Date(e._t).toISOString())} · ${band.label}`;
          return (
            <TrendDot
              key={i}
              cx={timeToX(e._t)}
              cy={vasToY(e.vas)}
              scale={0.8}
              onMouseEnter={(ev) => showTip(ev, text)}
              onMouseMove={(ev) => showTip(ev, text)}
              onMouseLeave={hideTip}
            />
          );
        })}

        {/* X-axis: start + end date. Kept simple; the audit rows carry
            entry-level dates already. */}
        <text x={PAD.l} y={H - 10} fontSize={10} fill="#6b7280">
          {formatShortDate(new Date(tMin).toISOString())}
        </text>
        <text x={PAD.l + IW} y={H - 10} textAnchor="end" fontSize={10} fill="#6b7280">
          {formatShortDate(new Date(tMax).toISOString())}
        </text>
      </svg>
      <TrendTip tip={tip} />
    </div>
  );
};

StatusOverTimeChart.propTypes = {
  audit: PropTypes.array,
};

export default StatusOverTimeChart;
